import React, { useEffect, useRef } from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import './PmToast.css';

export type PmToastKind = 'info' | 'success' | 'warning' | 'error';

export interface PmToastItem {
  id: string;
  kind: PmToastKind;
  title: string;
  body?: React.ReactNode;
  /** Auto-dismiss in ms; 0 disables. */
  autoDismissMs?: number;
}

interface PmToastStackProps {
  toasts: PmToastItem[];
  onDismiss: (id: string) => void;
}

const ICON: Record<PmToastKind, React.ReactNode> = {
  info: <Info size={18} aria-hidden />,
  success: <CheckCircle2 size={18} aria-hidden />,
  warning: <AlertTriangle size={18} aria-hidden />,
  error: <AlertTriangle size={18} aria-hidden />,
};

const DEFAULT_DISMISS_MS: Record<PmToastKind, number> = {
  info: 4000,
  success: 3000,
  warning: 6500,
  error: 7000,
};

const PmToastRow: React.FC<{ toast: PmToastItem; onDismiss: (id: string) => void }> = ({
  toast,
  onDismiss,
}) => {
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const ms = toast.autoDismissMs ?? DEFAULT_DISMISS_MS[toast.kind];
    if (!ms) return;
    timerRef.current = window.setTimeout(() => onDismiss(toast.id), ms);
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, [toast.id, toast.kind, toast.autoDismissMs, onDismiss]);

  return (
    <div
      className={`pm-toast pm-toast--${toast.kind}`}
      role={toast.kind === 'error' || toast.kind === 'warning' ? 'alert' : 'status'}
      aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
    >
      <span className="pm-toast-icon" aria-hidden>
        {ICON[toast.kind]}
      </span>
      <div className="pm-toast-body">
        <div className="pm-toast-title">{toast.title}</div>
        {toast.body ? <div className="pm-toast-detail">{toast.body}</div> : null}
      </div>
      <button
        type="button"
        className="pm-toast-close"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(toast.id)}
      >
        <X size={14} aria-hidden />
      </button>
    </div>
  );
};

const PmToastStack: React.FC<PmToastStackProps> = ({ toasts, onDismiss }) => {
  if (!toasts.length) return null;
  return (
    <div className="pm-toast-stack" aria-label="Notifications">
      {toasts.map((t) => (
        <PmToastRow key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

export default PmToastStack;
